import React, { useEffect } from 'react';
import storeBtnAr from '../assets/store-btn-ar.png';
import appIcon from '../assets/app-icon.png';
import { Download, Star, CheckCircle, Smile, Image as ImageIcon, Sparkles, Wand2, Type, LayoutGrid, Package, Send } from 'lucide-react';

const StickerMakerLanding = () => {
  const PLAY_STORE_LINK = "https://play.google.com/store/apps/details?id=com.arabic.stickermaker";

  useEffect(() => {
    document.title = "صانع الملصقات العربية | التطبيق الأقوى لتصميم ملصقات واتساب";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "حوّل صورك وأفكارك إلى ملصقات واتساب مضحكة واحترافية في ثوانٍ معدودة. التطبيق الأسهل والأكثر تكاملاً للمستخدم العربي.");
    }
  }, []);

  const handleDownload = () => {
    window.open(PLAY_STORE_LINK, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 font-sans" dir="rtl">
      {/* Navbar */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-6xl mx-auto bg-transparent">
        <div className="flex items-center gap-3">
          <img src={appIcon} alt="صانع الملصقات" className="w-10 h-10 rounded-xl shadow-lg object-cover" />
          <span className="font-bold text-xl text-slate-800">صانع الملصقات العربية</span>
        </div>
        <button 
          onClick={handleDownload}
          className="hover:scale-105 transition-transform"
        >
          <img src={storeBtnAr} alt="Get it on Google Play" className="h-10" />
        </button>
      </nav>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-6 py-12 md:py-24 flex flex-col md:flex-row items-center gap-12">
        <div className="flex-1 text-center md:text-right">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-bold mb-6">
            <Star className="w-4 h-4 fill-indigo-700" />
            التطبيق الأقوى لتصميم ملصقات واتساب!
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 mb-6 leading-tight">
            اصنع ملصقات واتساب مميزة <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">في ثوانٍ</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
            حوّل صورك وأفكارك إلى ملصقات واتساب مضحكة واحترافية في ثوانٍ معدودة. التطبيق الأسهل والأكثر تكاملاً للمستخدم العربي.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
            <button 
              onClick={handleDownload}
              className="hover:scale-105 transition-transform w-full sm:w-auto flex justify-center"
            >
              <img src={storeBtnAr} alt="Get it on Google Play" className="h-16" />
            </button>
          </div>
        </div>
        
        {/* Mockup Image/Graphic */}
        <div className="flex-1 relative">
          <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500 to-purple-500 rounded-full blur-3xl opacity-20 animate-pulse"></div>
          <div className="relative bg-white border-[8px] border-slate-900 rounded-[3rem] shadow-2xl p-4 w-64 mx-auto md:w-80 h-[500px] flex flex-col overflow-hidden">
            <div className="w-24 h-6 bg-slate-900 absolute top-0 left-1/2 -translate-x-1/2 rounded-b-xl"></div>
            <div className="flex-1 bg-slate-50 rounded-2xl flex flex-col items-center justify-center p-6 text-center border border-slate-100">
              <Smile className="w-24 h-24 text-indigo-400 mb-6" />
              <h3 className="font-bold text-xl text-slate-800 mb-2">اصنع ملصقك الآن</h3>
              <p className="text-sm text-slate-500">اختر صورة من معرض هاتفك لتبدأ</p>
              <div className="mt-8 w-full bg-indigo-600 text-white py-3 rounded-xl font-bold shadow-md">بدء التصميم</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-20 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">مميزات التطبيق بالتفصيل ✨</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">كل ما تحتاجه لإطلاق إبداعك وصنع ملصقات فريدة من نوعها.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-blue-100 group-hover:bg-blue-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-blue-600">
                <Wand2 className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">الممحاة السحرية 🪄</h3>
              <p className="text-slate-600 leading-relaxed text-sm">وداعاً للقص اليدوي المتعب! بضغطة زر واحدة، يقوم الذكاء الاصطناعي في التطبيق بإزالة خلفية الصور بدقة متناهية لتفريغ الصورة وجعلها جاهزة كملصق.</p>
            </div>
            
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-purple-100 group-hover:bg-purple-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-purple-600">
                <Type className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">مكتبة خطوط عربية ضخمة ✍️</h3>
              <p className="text-slate-600 leading-relaxed text-sm">اكتب على ملصقاتك بأجمل الخطوط العربية المتميزة والاحترافية. يمكنك تغيير ألوان النص، وإضافة حواف وظلال ليكون النص واضحاً وجذاباً.</p>
            </div>
            
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-pink-100 group-hover:bg-pink-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-pink-600">
                <Smile className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">مكتبة زخارف وعبارات 😂</h3>
              <p className="text-slate-600 leading-relaxed text-sm">أضف لمسة كوميدية وإبداعية لملصقاتك باستخدام مكتبة ضخمة من الزخارف، الوجوه التعبيرية، والعبارات العربية الجاهزة التي تناسب كل المواقف والترندات.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-orange-100 group-hover:bg-orange-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-orange-600">
                <LayoutGrid className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">قص حر ودائري ✂️</h3>
              <p className="text-slate-600 leading-relaxed text-sm">قص الصور يدوياً بشكل دائري أو حر بكل سهولة، مع إمكانية تدوير الصورة وضبط حجمها لتبدو مثالية في المحادثات.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-teal-100 group-hover:bg-teal-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-teal-600">
                <Package className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">إنشاء حزم ملصقات غير محدودة 📦</h3>
              <p className="text-slate-600 leading-relaxed text-sm">نظّم ملصقاتك في حزم خاصة بك (حزمة لأصدقاء العمل، حزمة ميمز، حزمة للعائلة... إلخ).</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-green-100 group-hover:bg-green-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-green-600">
                <Send className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">إضافة مباشرة إلى واتساب 📲</h3>
              <p className="text-slate-600 leading-relaxed text-sm">بمجرد الانتهاء من تصميم ملصقاتك، أضفها فوراً إلى تطبيق واتساب وتكون جاهزة للمشاركة مع الجميع.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-slate-900 py-20 text-center px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">هل أنت مستعد لصنع ملصقاتك الخاصة؟</h2>
        <p className="text-slate-400 mb-10 max-w-2xl mx-auto text-lg">انضم إلى آلاف المستخدمين وابدأ بتصميم ملصقاتك اليوم.</p>
        <button 
          onClick={handleDownload}
          className="hover:scale-105 transition-transform inline-flex"
        >
          <img src={storeBtnAr} alt="Get it on Google Play" className="h-16" />
        </button>
      </div>
      
      {/* Footer */}
      <footer className="bg-slate-950 py-6 text-center border-t border-slate-800 text-slate-500 text-sm">
        جميع الحقوق محفوظة © صانع الملصقات العربية 2026
      </footer>
    </div>
  );
};

export default StickerMakerLanding;
